import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "wouter";
import { AlertTriangle, ArrowLeft, CheckCircle, Database, FileText, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CountryDisplay } from "@/components/ui/country-combobox";
import { formatSectorLabel } from "@/lib/sector-labels";
import type { DashboardOverview } from "@/hooks/use-dashboard";

interface AssessmentOverview {
  company: { id: string; name: string; country: string | null; sector: string | null };
  // Same object as GET /api/dashboard/overview's currentAssessment (dashboardService.buildAssessmentStatus).
  currentStatus: DashboardOverview["currentAssessment"] & {
    name: string | null;
    periodStart: string;
    periodEnd: string;
  };
  dataAlerts: DashboardOverview["dataAlerts"];
  // assessmentService.getAssessmentCompletion: card key -> percent.
  completion: Record<string, number>;
}

async function fetchOverview(assessmentId: string): Promise<AssessmentOverview> {
  const res = await fetch(`/api/portfolio/assessments/${assessmentId}/overview`, { credentials: "include" });
  if (res.status === 404) throw new Error("This assessment does not exist.");
  if (res.status === 403) throw new Error("You don't have access to this assessment.");
  if (!res.ok) throw new Error("Failed to load the assessment. Please refresh.");
  return res.json();
}

// Tab -> card grouping matches dashboard.service.ts's resume-step tabs (Environment has 5 cards
// there, without Climate risk management). Card labels match the workflow tab components.
const TABS: Array<{ label: string; cards: Array<{ key: string; label: string }> }> = [
  {
    label: "General",
    cards: [
      { key: "generalInfoCompletion", label: "General Information" },
      { key: "sustainabilityCompletion", label: "Policies & targets" },
    ],
  },
  {
    label: "Environment",
    cards: [
      { key: "carbonCompletion", label: "Carbon Emissions" },
      { key: "pollutionCompletion", label: "Pollution of Air, Water and Soil" },
      { key: "biodiversityCompletion", label: "Biodiversity" },
      { key: "waterCompletion", label: "Water" },
      { key: "wasteCompletion", label: "Resource Use, Circular Economy & Waste" },
    ],
  },
  {
    label: "Social",
    cards: [
      { key: "workforceCompletion", label: "Employee Support & Wellbeing" },
      { key: "diversityInclusionCompletion", label: "Diversity & Inclusion" },
      { key: "humanRightsCompletion", label: "Human Rights" },
      { key: "communityContributionCompletion", label: "Community Contribution" },
    ],
  },
  {
    label: "Governance",
    cards: [
      { key: "ethicsCompletion", label: "Ethics & Integrity" },
      { key: "transparencyCompletion", label: "Transparency & Disclosure" },
      { key: "leadershipCompletion", label: "Leadership & Governance" },
      { key: "accountabilityCompletion", label: "Accountability & Reporting" },
    ],
  },
];

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

const formatTco2e = (value: number | null) => (value != null ? value.toFixed(3) : "—");

function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`p-6 bg-card rounded-3xl shadow-sm outline outline-1 outline-offset-[-1px] outline-border ${className}`}>
      {children}
    </div>
  );
}

function ProgressBar({ percent, className = "h-2" }: { percent: number; className?: string }) {
  return (
    <div className={`w-full bg-slate-100 rounded-full overflow-hidden ${className}`}>
      <div className="h-full bg-primary rounded-full" style={{ width: `${percent}%` }} />
    </div>
  );
}

export default function PortfolioAssessmentOverview() {
  const { assessmentId } = useParams<{ assessmentId: string }>();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [`/api/portfolio/assessments/${assessmentId}/overview`],
    queryFn: () => fetchOverview(assessmentId),
    enabled: !!assessmentId,
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4 text-center">
        <p className="text-sm text-slate-500">
          {error?.message ?? "Failed to load the assessment. Please refresh."}
        </p>
        <Link href="/portfolio/assessments" asChild>
          <Button variant="outline" className="rounded-2xl">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to assessments
          </Button>
        </Link>
      </div>
    );
  }

  const { company, currentStatus, dataAlerts, completion } = data;
  const locked = currentStatus.status === "completed";
  const completeness = currentStatus.completenessPercent;

  const kpis = [
    { label: "Total emissions", value: currentStatus.totalEmissionsTco2e },
    { label: "Scope 1", value: currentStatus.scope1Tco2e },
    { label: "Scope 2", value: currentStatus.scope2Tco2e },
    { label: "Scope 3", value: currentStatus.scope3Tco2e },
  ];

  return (
    <div className="space-y-8">
      <Link href="/portfolio/assessments" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to assessments
      </Link>

      {/* Header */}
      <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <div className="space-y-2">
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">{company.name}</h1>
          <p className="text-slate-500">
            {`${currentStatus.name ?? "Assessment"} · ${formatDate(currentStatus.periodStart)} - ${formatDate(currentStatus.periodEnd)}`}
          </p>
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
                locked ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-600"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${locked ? "bg-emerald-500" : "bg-slate-400"}`} />
              {locked ? "Finalised" : "In progress"}
            </span>
            {company.sector && (
              <span className="px-2.5 py-1 bg-primary/10 rounded-full text-primary text-xs font-bold capitalize">
                {formatSectorLabel(company.sector)}
              </span>
            )}
            {company.country && <CountryDisplay value={company.country} />}
          </div>
          <div className="flex items-center gap-3 pt-2">
            <div className="w-48">
              <ProgressBar percent={completeness} />
            </div>
            <span className="text-sm font-semibold text-slate-700">{`${completeness}% complete`}</span>
          </div>
        </div>

        <div className="flex flex-col items-start gap-2 md:items-end">
          <div className="flex flex-wrap gap-2">
            <Link href={`/workflow/${assessmentId}`} asChild>
              <Button variant="outline" className="rounded-2xl">
                <Database className="h-4 w-4 mr-2" />
                Open data (read-only)
              </Button>
            </Link>
            {locked && (
              <Link href={`/portfolio/reports/${assessmentId}`} asChild>
                <Button className="rounded-2xl bg-primary hover:bg-primary/80 font-bold">
                  <FileText className="h-4 w-4 mr-2" />
                  View report
                </Button>
              </Link>
            )}
          </div>
          {!locked && <p className="text-sm text-muted-foreground">Report available once finalised</p>}
        </div>
      </div>

      {/* Emissions KPIs */}
      <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-muted-foreground">{kpi.label}</span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-3xl font-bold text-foreground">{formatTco2e(kpi.value)}</span>
              {kpi.value != null && <span className="text-sm text-muted-foreground font-medium">tCO₂e</span>}
            </div>
          </Card>
        ))}
      </div>

      {/* Per-tab progress */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {TABS.map((tab) => {
          const tabPercent = Math.round(
            tab.cards.reduce((sum, card) => sum + (completion[card.key] ?? 0), 0) / tab.cards.length
          );
          return (
            <Card key={tab.label}>
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-lg font-bold text-foreground">{tab.label}</h3>
                <span className="text-lg font-bold text-foreground">{`${tabPercent}%`}</span>
              </div>
              <ProgressBar percent={tabPercent} />
              <div className="mt-5 space-y-3">
                {tab.cards.map((card) => {
                  const cardPercent = completion[card.key] ?? 0;
                  return (
                    <div key={card.key}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">{card.label}</span>
                        <span className="font-medium text-slate-700">{`${cardPercent}%`}</span>
                      </div>
                      <ProgressBar percent={cardPercent} className="h-1.5" />
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Data alerts */}
      <Card>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center">
            {dataAlerts.length === 0 ? (
              <CheckCircle className="h-5 w-5 text-primary" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-500" />
            )}
          </div>
          <h3 className="text-xl font-bold text-foreground">Data alerts</h3>
        </div>
        {dataAlerts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No data alerts.</p>
        ) : (
          <div className="space-y-3">
            {dataAlerts.map((alert, i) => {
              const isWarning = alert.severity === "warning";
              return (
                <div
                  key={i}
                  className={`flex items-start gap-3 p-4 rounded-2xl border ${
                    isWarning ? "bg-red-50 border-red-100" : "bg-amber-50 border-amber-100"
                  }`}
                >
                  {isWarning ? (
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-red-600" />
                  ) : (
                    <Info className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${isWarning ? "text-red-900" : "text-amber-900"}`}>
                      {alert.message}
                    </p>
                    <Link
                      href={`/workflow/${assessmentId}?tab=${alert.workflowTab}`}
                      className="text-xs underline font-medium mt-1 inline-block hover:opacity-80"
                    >
                      View in workflow →
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
